<script setup lang="ts">
import { defineProps } from 'vue'
import type { Partner } from '@/api-swagger'
import PartnerTypeTag from './PartnerTypeTag.vue'
import { printCurrency } from '@/utils/numbers'
import CreditSafeValue from '@/components/common/ui/CreditSafeValue.vue'
import IconInfo from '@/assets/icons/info.svg'
import PartnerAction from '@/components/features/partners/PartnerAction.vue'
import PartnerEdit from '@/components/features/partners/PartnerEdit.vue'
import PartnerButtonRedirect from '@/components/common/buttons/PartnerButtonRedirect.vue'
import ParentAttribution from '@/components/features/partners/ParentAttribution.vue'
import { LinkSlashIcon, WindowIcon, EyeIcon, MapIcon } from '@heroicons/vue/24/outline'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useToast } from 'vue-toast-notification'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import { storeToRefs } from 'pinia'
import PartnerLogoImage from '@/components/common/ui/PartnerLogoImage.vue'

const partnerStore = usePartnerStore()
const $toast = useToast()

const opportunityStore = useOpportunityStore()
const { partnerIsActiveOpportunities } = storeToRefs(opportunityStore)

const props = defineProps({
  partner: {
    type: Object as () => Partner | null,
    default: null,
    required: false,
  },
  company: {
    type: Object as () => any | null,
    default: null,
    required: false,
  }
})
const unlink = async () => {
  if (!props.partner) return
  if (props.partner.id) {
    await partnerStore
      .attributionParent(props.partner.id, { parentId: 0 })
      .then(() => {
        $toast.success('Mise a jour effectuée')
      })
      .catch((error: { message: string }) => {
        $toast.error(error.message)
      })
  }
}
</script>

<template>
  <div
    id="partner-title-block"
    class="flex gap-6 justify-between items-start w-full xl:w-4/5 self-center"
  >
    <div class="flex gap-6 mb-4 w-1/2">
      <div class="flex flex-col items-center gap-4">
        <div
          v-if="partner"
          class="card bg-white rounded-lg w-24 h-24 p-6 flex justify-center items-center"
        >
          <partner-logo-image :image1920="partner.image1920" :name="partner.name" />
        </div>
        <div v-else class="skeleton opacity-30 w-24 h-24"></div>

        <PartnerTypeTag v-if="partner" :params="{ partner }" />
        <div v-else-if="!partner" class="skeleton opacity-30 badge badge-info text-base-100"></div>
      </div>
      <div class="flex flex-col justify-between">
        <p v-if="partner" class="flex items-center gap-1 flex-wrap">
          <span class="font-bold text-nowrap">
            {{ partner.name }}
          </span>
          <PartnerEdit :partner="partner" />
          <a
            v-if="(partner as any).odooUrl"
            :href="(partner as any).odooUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="tooltip tooltip-right"
            data-tip="Voir dans Odoo"
          >
            <EyeIcon class="w-4 h-4 text-base-content/40 hover:text-primary transition-colors" />
          </a>
        </p>
        <p v-else class="skeleton opacity-30 w-64 h-8 font-bold text-nowrap"></p>
        <div v-if="partner" class="flex items-center gap-1 font-bold">
          <span class="text-nowrap">{{ partner.street }} {{ partner.zip }} {{ partner.city }}</span>
          <a
            v-if="partner.street || partner.city"
            :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([partner.street, partner.zip, partner.city].filter(Boolean).join(' '))}`"
            target="_blank"
            rel="noopener noreferrer"
            class="tooltip tooltip-right"
            data-tip="Voir sur Google Maps"
          >
            <MapIcon class="w-4 h-4 text-base-content/40 hover:text-primary transition-colors" />
          </a>
        </div>
        <p v-else class="skeleton opacity-30 w-64 h-8 font-bold text-nowrap"></p>
        <div class="partner-info">
          <div v-if="partner" class="my-2 flex items-center gap-1.5 font-bold">
            <span>SIRET : {{ partner.siret || '' }}</span>
            <a
              v-if="partner.siret"
              :href="`https://www.pappers.fr/recherche?q=${partner.siret}`"
              target="_blank"
              rel="noopener noreferrer"
              class="tooltip tooltip-right"
              data-tip="Rechercher sur Pappers"
            >
              <WindowIcon class="w-4 h-4 text-base-content/40 hover:text-primary transition-colors" />
            </a>
          </div>
          <p v-else class="skeleton opacity-30 w-full h-8 font-bold my-2"></p>

          <div v-if="partner" class="flex items-center gap-2">
            <div>
              <p class="font-bold my-2">Société mère :</p>
            </div>
            <partner-button-redirect
              v-if="partner.parentId?.name"
              :partner-name="partner.parentId?.name"
              :partner-id="partner.parentId?.id"
            ></partner-button-redirect>
            <ButtonDaisy
              v-if="partner.parentId?.siret"
              text="Délier"
              :icon="LinkSlashIcon"
              variant="secondary"
              @click="unlink"
            />
            <parent-attribution v-else />
          </div>
          <div
            v-else
            class="skeleton opacity-30 w-full h-8 flex justify-between items-center my-2"
          ></div>
        </div>

        <PartnerAction
          v-if="partner"
          :partner="partner"
          :company="company"
          :company-uuid="company?.uuid || ''"
          geoloc
          activity
          class="mt-2 mr-auto partner-info"
        />
        <div v-else class="skeleton opacity-30 w-full h-8"></div>
      </div>
    </div>
    <div class="flex gap-6 mb-4 items-center">
      <p v-if="partner?.xCreditStatus" class="font-bold my-2">
        {{ partner?.xCreditStatus }}
      </p>
      <div class="flex flex-col gap-2" v-if="partner">
        <div class="card bg-white flex flex-row gap-4 w-full py-4 px-6">
          <div class="flex flex-col w-10/12 items-start pe-7 border-r border-gray-300">
            <p class="text-base-content text-sm">CA facturé</p>
            <p class="text-3xl font-bold text-accent">
              {{ printCurrency(partner.totalInvoiced, false) }}
            </p>
          </div>

          <div class="max-w-fit items-start">
            <p class="text-base-content text-sm">Confiance</p>
            <credit-safe-value
              class="text-3xl text-primary"
              :credit-safe-value="partner.creditSafeValue"
            ></credit-safe-value>
          </div>
        </div>
        <div
          class="card p-4 flex flex-row items-center gap-4 bg-error"
          v-if="partner.saleWarn != 'no-message'"
        >
          <div class="tooltip tooltip-top" :data-tip="partner.saleWarnMsg">
            <component :is="IconInfo" class="fill-current text-content" width="20" />
          </div>

          <div>
            <p class="text-lg font-bold">Attention</p>
            <p class="text-sm">{{ partner.saleWarnMsg }}</p>
          </div>
        </div>

        <div v-if="partnerIsActiveOpportunities">
          <div class="card p-4 flex flex-row items-center gap-4 bg-warning">
            Opportunité en cours par un commercial
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
#partner-title-block {
  .partner-info {
    .btn {
      background-color: white;
      border: none;
      box-shadow: none;
    }
    .btn:hover {
      background-color: var(--color-primary);
      color: var(--color-primary-content);
    }
  }
}
</style>
